import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@ember-data/model';
import { service } from '@ember/service';
import type { Type } from '@warp-drive/core-types/symbols';
import type IntlService from 'ember-intl/services/intl';
import nameSort from 'scaleflow/utils/nameSort';
import type InitiativeModel from './initiative';
import type OwnerModel from './owner';
import type TargetModel from './target';

type DeltaLike = { ops?: { insert?: unknown }[] };

export default class GoalModel extends Model {
  declare [Type]: 'goal';
  @service declare intl: IntlService;

  @attr('string') declare title: string;
  @hasMany('initiative', {
    async: true,
    inverse: 'goals',
  })
  declare initiatives: AsyncHasMany<InitiativeModel>;
  @hasMany('target', {
    async: true,
    inverse: 'goal',
  })
  declare targets: AsyncHasMany<TargetModel>;
  @hasMany('goal', { async: true, inverse: 'parent' })
  declare children: AsyncHasMany<GoalModel>;
  @belongsTo('goal', { async: true, inverse: 'children' })
  declare parent: AsyncBelongsTo<GoalModel>;
  @attr('boolean', { defaultValue: true }) declare active: boolean;

  @belongsTo('owner', {
    async: true,
    inverse: 'goals',
    polymorphic: true,
  })
  declare owner: AsyncBelongsTo<OwnerModel>;

  @attr declare description: DeltaLike | null;

  get metrics() {
    const targets = this.targets.content ?? [];
    return targets.map((item) => item.metric);
  }

  get changeset_object() {
    return {
      title: this.title,
      targets: this.targets.content,
      children: this.children.content,
      parent: this.parent.content,
      owner: this.owner.content,
      description: this.description,
      active: this.active,
    };
  }

  mapChangesetToAttributes(changes: { key: string; value: unknown }[]) {
    changes.forEach(({ key, value }) => {
      (this as unknown as Record<string, unknown>)[key] = value;
    });
  }

  get activeLabel() {
    return this.intl.t(`_goals.status.${this.active}`);
  }

  get sortedChildren() {
    return [...(this.children.content ?? [])].sort((a, b) =>
      nameSort(a.title, b.title),
    );
  }

  get hasMoreThanOneInitiative() {
    const initiatives = this.initiatives.content ?? [];
    return initiatives.length > 1 ? initiatives.length - 1 : false;
  }

  get initiativeIds(): (string | null)[] {
    return (this as GoalModel).hasMany('initiatives')?.ids();
  }

  get textualDescription() {
    // Check if delta or ops is undefined/empty
    if (!this.description || !this.description.ops) {
      return 'No description available';
    }

    // Map through the ops and extract only the insert strings
    const plainTextParts = this.description.ops
      .map((op) => op.insert)
      .filter((insert) => insert && typeof insert === 'string');

    // Join the parts and trim any excess whitespace
    return plainTextParts.join('').trim();
  }
}
